// Lucra Photography — content constants

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/clients", label: "Clients" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_e833408a-3c94-42b0-9396-a0af76475d0b/artifacts/wjgsoe2h_324F9A8D-1835-403C-8B46-B999FDFB6767-2c7e694c.png";

// Real client imagery — used throughout the site in place of stock
export const CLIENT_IMAGES = [
  "https://customer-assets-jt897jd0.emergentagent.net/job_cranky-allen-14/artifacts/xr3t6i6t_IMG_8929.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_cranky-allen-14/artifacts/q9zky8t7_IMG_8935.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_cranky-allen-14/artifacts/z9hm8bvb_IMG_8944.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_cranky-allen-14/artifacts/4dkc918s_IMG_8925.webp",
];

export const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/e833408a-3c94-42b0-9396-a0af76475d0b/images/a21c57f4a7f6e9fa4bdcd371534915a179bbcae8886bbbcd10520f550f862205.png";

export const CITY_LIGHTS = CLIENT_IMAGES[1];

// ----- Signature Partnership Program (the core service) -----
export const PROCESS = [
  {
    tag: "01",
    title: "We Show Up",
    body:
      "Our team arrives during peak hours, special events, and signature nights — fully kitted, dressed for the room, ready to disappear into the crowd.",
  },
  {
    tag: "02",
    title: "We Capture Moments",
    body:
      "Families, friends, dates, and celebrations — real moments in real time. No staged poses unless guests ask for one.",
  },
  {
    tag: "03",
    title: "We Print & Frame On-Site",
    body:
      "Premium-quality prints, hand-framed at the venue. Same-night, in-hand delivery — guests leave with the memory already on their wall.",
  },
  {
    tag: "04",
    title: "We Sell to Guests",
    body:
      "Guests purchase their framed photos directly as keepsakes. Zero overhead for the venue, pure upside, and a story your guests retell every time someone notices the frame.",
  },
];

// Portfolio content is now pulled live from /api/clients/rotating.
// No fabricated titles or specs.

// ----- Custom productions (secondary services) -----
export const SERVICES = [
  {
    slug: "partnership",
    name: "Signature Partnership Program",
    tag: "00",
    short: "On-site photography that turns venues into experiences.",
    description:
      "Our flagship offering. We partner with restaurants, lounges, hotels, and venues to provide on-site photography that delights guests and creates a new revenue moment for your business — zero overhead, no setup cost.",
    deliverables: [
      "On-site photographer during peak hours",
      "Mobile print & frame station",
      "Same-night, in-hand delivery to guests",
      "Direct-to-guest keepsake sales",
      "Optional venue-branded frames",
    ],
    hero: CLIENT_IMAGES[0],
  },
  {
    slug: "weddings",
    name: "Weddings",
    tag: "01",
    short: "A wedding is a current. We move with it.",
    description:
      "Cinematic, full-day coverage built around the people, not the timeline. Two photographers, one motion operator, hand-curated galleries delivered fast.",
    deliverables: ["Full-day coverage", "Two photographers", "Curated image gallery", "Highlight reel"],
    hero: CLIENT_IMAGES[1],
  },
  {
    slug: "portraits",
    name: "Portraits",
    tag: "02",
    short: "Edge-lit, considered, unmistakably yours.",
    description:
      "Studio or location sessions for individuals, couples, and families. We design light around your face — not around the room.",
    deliverables: ["Studio or location", "Wardrobe consultation", "Retouched gallery", "Print release"],
    hero: CLIENT_IMAGES[2],
  },
  {
    slug: "events",
    name: "Events",
    tag: "03",
    short: "Launches, galas, after-hours.",
    description:
      "Coverage that captures atmosphere, motion, and brand simultaneously. Same-night social edits available for premium activations.",
    deliverables: ["Lead photographer", "Same-night previews", "Branded delivery portal", "Press-ready exports"],
    hero: CLIENT_IMAGES[3],
  },
  {
    slug: "real-estate",
    name: "Real Estate",
    tag: "04",
    short: "Listings that feel like editorial.",
    description:
      "Architectural photography, twilight exteriors, and drone work for properties that deserve more than a wide-angle phone snap.",
    deliverables: ["HDR interior frames", "Twilight exterior", "Drone aerials", "MLS-ready exports"],
    hero: CLIENT_IMAGES[0],
  },
  {
    slug: "commercial",
    name: "Commercial & Brand",
    tag: "05",
    short: "Visual systems for ambitious brands.",
    description:
      "Product, lifestyle, founder portraits, and content libraries. We shoot for the deck, the storefront, and the algorithm — all at once.",
    deliverables: ["Pre-production & moodboards", "Full lighting kit", "Same-week web exports", "Usage licensing"],
    hero: CLIENT_IMAGES[2],
  },
  {
    slug: "fashion",
    name: "Fashion & Editorial",
    tag: "06",
    short: "For lookbooks, magazines, and brave designers.",
    description:
      "Editorial direction, casting collaboration, set design, and on-set styling support. We treat every frame like a cover.",
    deliverables: ["Creative direction", "Studio or location", "Multiple looks", "High-end retouching"],
    hero: CLIENT_IMAGES[1],
  },
];

// ----- Engagement models (replaces pricing tiers, no numbers) -----
export const ENGAGEMENT_MODELS = [
  {
    name: "Partnership",
    label: "Recurring",
    summary:
      "For restaurants, lounges, hotels, and venues. We bring the team, the kit, and the prints — your guests buy directly. Built for ongoing nights, not one-offs.",
    features: [
      "On-site photographer + print station",
      "Same-night framed delivery",
      "Direct-to-guest sales handled by us",
      "No setup fee, no overhead for the venue",
      "Recurring weekly or monthly cadence",
    ],
  },
  {
    name: "Signature",
    label: "Most Requested",
    summary:
      "Events, brand shoots, editorials. Our most-requested production model — full creative direction with a small, decisive team.",
    features: [
      "Lead photographer + assistant",
      "Same-week social edits",
      "Curated retouched gallery",
      "Branded delivery portal",
      "On-set creative direction",
    ],
    featured: true,
  },
  {
    name: "Legacy",
    label: "Bespoke",
    summary:
      "Weddings, multi-day productions, and creative campaigns that demand full creative direction across stills and motion.",
    features: [
      "Full-day or multi-day coverage",
      "Two photographers + motion op",
      "Cinematic highlight film",
      "Heirloom-quality curated gallery",
      "Optional printed album add-on",
    ],
  },
];

export const VALUES = [
  {
    title: "Cinematic Craft",
    body: "We shape every frame with intent — light, motion, and silence are tools, not accidents.",
  },
  {
    title: "Quiet Luxury",
    body: "No logos on caps. No flash and grab. We arrive prepared, dressed for the room, ready to disappear.",
  },
  {
    title: "Partnership-Driven",
    body: "Planners, venues, and brands trust us because we make them look good without making it about us.",
  },
  {
    title: "Future-Native",
    body: "Same-day workflows, on-site framing stations, drone aerials, and color science built for OLED viewing.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Lucra didn’t just photograph our night — they turned it into a revenue moment. Guests left holding framed memories of themselves. We rebooked them within the week.",
    name: "The Halcyon Lounge",
    role: "Buckhead, Atlanta",
  },
  {
    quote:
      "We hired Lucra for a brand relaunch in D.C. and got a full asset library back in eight days. The deck practically wrote itself.",
    name: "Halcyon Studio",
    role: "Creative Director, DC",
  },
  {
    quote:
      "They moved through the venue like ghosts and somehow caught every laugh, every glance. Our guests are still texting us their framed photos.",
    name: "Olivia P.",
    role: "Georgetown, DC",
  },
];

export const STATS = [
  { value: "Peak", label: "Hours covered" },
  { value: "Same-Night", label: "Print & frame" },
  { value: "ATL / DC", label: "Home cities" },
  { value: "Zero", label: "Setup cost to venues" },
];

export const CITIES = [
  {
    name: "Atlanta",
    code: "ATL",
    state: "Georgia",
    coords: "33.7490° N · 84.3880° W",
    blurb:
      "Heat, glass, and motion. Buckhead rooftops, Westside warehouses, and Piedmont light.",
  },
  {
    name: "Washington",
    code: "DC",
    state: "District of Columbia",
    coords: "38.9072° N · 77.0369° W",
    blurb:
      "Stone, history, and quiet authority. Georgetown townhouses, embassy gardens, monuments at dusk.",
  },
];

export const PARTNERS = [
  "RESTAURANTS", "LOUNGES", "NIGHTCLUBS", "ROOFTOP BARS",
  "HOTELS", "REAL ESTATE", "BRAND ACTIVATIONS", "PRIVATE EVENTS",
  "WEDDINGS", "GALAS", "POP-UPS", "VENUE PARTNERSHIPS",
];
